import React, { useState, useEffect, useRef } from 'react';

interface Benefit {
  icon: React.ReactNode;
  text: string;
}

interface InpartBenefitsMobileProps {
  benefits: Benefit[];
}

export default function InpartBenefitsMobile({ benefits }: InpartBenefitsMobileProps) {
  const [currentBenefit, setCurrentBenefit] = useState(0);
  const [prevBenefit, setPrevBenefit] = useState<number | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goTo = (idx: number) => {
    if (idx === currentBenefit) return;
    setPrevBenefit(currentBenefit);
    setCurrentBenefit(idx);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => setPrevBenefit(null), 300);
  };

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setPrevBenefit(currentBenefit);
      setCurrentBenefit(prev => (prev + 1) % benefits.length);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setPrevBenefit(null), 300);
    }, 3000);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [currentBenefit, benefits.length]);

  return (
    <aside className="bg-content-primary text-content-inverted rounded-lg p-4 sm:p-8 flex flex-col gap-4 w-full">
      <h2 className="text-body-xl font-bold text-content-inverted leading-body-xl">Why Join Inpart?</h2>

      <div className="relative w-full min-h-15 block sm:hidden overflow-hidden">
        {benefits.map((b, idx) => {
          const isActive = idx === currentBenefit;
          const isExiting = idx === prevBenefit;

          return (
            <div
              key={idx}
              className={`absolute inset-0 text-left transition-all duration-300 ease-in-out
                ${
                  isActive
                    ? 'opacity-100 translate-x-0 visible'
                    : isExiting
                    ? 'opacity-0 -translate-x-6 visible'
                    : 'opacity-0 translate-x-6 invisible'
                }
              `}
            >
              <div className="flex items-start gap-4 max-h-15">
                {b.icon}
                <span className="text-body-lg leading-highlights-lg max-h-15">{b.text}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="hidden sm:block">
        <div className="flex flex-col gap-4">
          {benefits.map((b, idx) => (
            <div key={idx} className="text-left">
              <div className="flex items-start gap-6">
                {b.icon}
                <span className="text-body-lg leading-highlights-lg">{b.text}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex sm:hidden justify-center mt-3 gap-2">
        {benefits.map((_, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => goTo(idx)}
            className={`w-2.5 h-2.5 rounded-full transition-colors ${
              idx === currentBenefit ? 'bg-content-brand' : 'bg-white'
            }`}
            aria-label={`Show benefit ${idx + 1}`}
          />
        ))}
      </div>
    </aside>
  );
}
